// ============================================================
// ExportCaseDialog.tsx
// The ONE "Export case" dialog (approved mockup casevo-case-export-redesign.html
// §A/§B) — replaces the PDF-only ExportPdfDialog. A format choice at the top:
//   • PDF summary — every existing option kept: detail (Summary / Full), the
//     6 sections, also include witnesses / deadlines, firm letterhead, hide
//     empty fields, and Dark background under "More options". "Export PDF"
//     builds the branded summary (exportCasePdf) and opens it to print.
//   • CSV data — what's in the file (the 128 flat case columns) + the
//     filename (casevo-<REF>-<date>.csv). "Download CSV" = the existing
//     direct downloadCasesCsv, one row for this case.
// Built on the shared ModalFrame (hero band + footer band). Opened from the
// case hero's "Export" button and the right-click "Export as PDF… / CSV…".
// ============================================================
import React, { useEffect, useMemo, useState } from 'react'
import ModalFrame, { HERO_PILL_SOFT } from './ModalFrame'
import type { Witness, RawCase } from '../../types/case.types'
import type { CaseDeadline } from '../../types/deadlines.types'
import { CASE_SECTION_GROUPS, DEFAULT_SECTION_KEYS } from '../../utils/caseSectionGroups'
import { exportCasePdf, CasePdfOptions } from '../../utils/exportCasePdf'
import { CASE_EXPORT_COLUMNS, caseCsvFilename, downloadCasesCsv } from '../../utils/exportCasesCsv'
import { caseName, caseRef } from '../../utils/caseName'
import { useCasevoStore } from '../../store/useCasevoStore'
import { getFirmLogoApi } from '../../api/casevoApi'

export type ExportFormat = 'pdf' | 'csv'

interface ExportCaseDialogProps {
  isOpen: boolean
  onClose: () => void
  // The FLAT case record (flattenCase key set + id/createdAt; matterNumber +
  // assignedTo when known). Prop-driven so it exports ANY case.
  caseFlat: Record<string, unknown>
  witnesses: Witness[]
  deadlines: CaseDeadline[]
  firmName?: string | null
  // Which format card is selected on open (menu "Export as CSV…" → 'csv').
  initialFormat?: ExportFormat
}

// ── icons (Feather) ──
const P = {
  dl: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><path d="M12 15V3" /></>,
  pdf: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 15h6" /><path d="M9 11h2" /></>,
  csv: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h8" /><path d="M12 11v8" /></>,
  ext: <><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></>,
  chk: <polyline points="20 6 9 17 4 12" />,
  info: <><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></>,
  chev: <polyline points="6 9 12 15 18 9" />,
}
const Ic: React.FC<{ d: React.ReactNode; className?: string }> = ({ d, className = 'w-[14px] h-[14px]' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`shrink-0 ${className}`}>{d}</svg>
)

// ── mockup class strings (casevo-case-export-redesign.html :35-82) ──
const EB = 'text-[10.5px] font-bold tracking-[0.16em] uppercase text-casevo-text-soft mb-[8px]'                         // .eb
const FC = 'flex gap-[12px] items-start text-left border rounded-[12px] px-[14px] py-[12px] transition-colors focus:outline-none casevo-focus' // .fc
const FC_ON = 'border-casevo-brand shadow-[0_0_0_1px_var(--casevo-brand)] bg-[color-mix(in_srgb,var(--casevo-mint)_45%,var(--casevo-surface))]'
const FC_OFF = 'border-casevo-border bg-casevo-surface hover:border-casevo-mid'
const FC_IC = 'w-[34px] h-[34px] rounded-[9px] grid place-items-center shrink-0'                                       // .fc .ic
const CHIP = 'flex items-center gap-[9px] border rounded-[10px] px-[11px] py-[9px] text-[13px] text-casevo-forest text-left transition-colors focus:outline-none casevo-focus' // .chip
const CHIP_ON = 'border-[color-mix(in_srgb,var(--casevo-brand)_55%,var(--casevo-border))] bg-casevo-surface'
const CHIP_OFF = 'border-casevo-border bg-casevo-surface hover:border-casevo-mid'
const CB = 'w-[17px] h-[17px] rounded-[5px] border-[1.5px] grid place-items-center shrink-0 text-white'                 // .cb
const TOG = 'flex items-center gap-[12px] py-[10px] border-t border-casevo-border-soft text-[13.5px] text-casevo-forest' // .tog
const BTN2 = 'inline-flex items-center text-[14px] font-semibold rounded-[9px] px-[18px] py-[9px] border border-casevo-border bg-casevo-surface text-casevo-mid hover:border-casevo-mid hover:text-casevo-forest transition-colors focus:outline-none casevo-focus' // .btn2
const BTN1 = 'inline-flex items-center gap-[7px] text-[14px] font-semibold rounded-[9px] px-[20px] py-[10px] bg-casevo-primary text-white hover:bg-casevo-primary-hover transition-colors focus:outline-none casevo-focus disabled:opacity-50 disabled:cursor-not-allowed' // .btn1

// The switch (.sw :64-69) — 36×20 track, 16px knob.
const Switch: React.FC<{ on: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string }> = ({ on, disabled, onChange, label }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)}
    className={`ml-auto relative w-[36px] h-[20px] rounded-full shrink-0 transition-colors focus:outline-none casevo-focus disabled:opacity-[0.45] disabled:cursor-not-allowed ${
      on ? 'bg-casevo-primary dark:bg-casevo-mid' : 'bg-casevo-border'}`}>
    <span className={`absolute top-[2px] w-[16px] h-[16px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-[left] ${on ? 'left-[18px]' : 'left-[2px]'}`} />
  </button>
)

// A toggle chip (.chip + .cb) used for sections / also-include.
const Chip: React.FC<{ on: boolean; label: string; n: string; onToggle: () => void }> = ({ on, label, n, onToggle }) => (
  <button type="button" role="checkbox" aria-checked={on} onClick={onToggle} className={`${CHIP} ${on ? CHIP_ON : CHIP_OFF}`}>
    <span className={`${CB} ${on ? 'bg-casevo-primary border-casevo-primary' : 'border-casevo-border'}`}>{on && <Ic d={P.chk} className="w-[12px] h-[12px]" />}</span>
    <span className="min-w-0">{label}</span>
    <span className="ml-auto text-[11px] text-casevo-text-soft">{n}</span>
  </button>
)

// CSV column preview (mockup :234) — the headline columns, then "+ N more".
const CSV_PREVIEW = ['id', 'matterNumber', 'caseName', 'openDate', 'clientForename', 'clientSurname', 'accidentDate', 'defendantName', 'tpiInsurer']

const ExportCaseDialog: React.FC<ExportCaseDialogProps> = ({
  isOpen, onClose, caseFlat, witnesses, deadlines, firmName, initialFormat = 'pdf',
}) => {
  const authUser = useCasevoStore((s) => s.authUser)
  const [format, setFormat] = useState<ExportFormat>(initialFormat)
  const [detailLevel, setDetailLevel] = useState<'summary' | 'full'>('summary')
  const [sections, setSections] = useState<Set<string>>(new Set(DEFAULT_SECTION_KEYS))
  const [includeWitnesses, setIncludeWitnesses] = useState(false)
  const [includeDeadlines, setIncludeDeadlines] = useState(false)
  const [hideEmpty, setHideEmpty] = useState(true)
  // Letterhead defaults ON only when a firm name is available.
  const [letterhead, setLetterhead] = useState(!!firmName)
  const [dark, setDark] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  // The firm logo for the letterhead — loaded when the dialog opens (the PDF
  // tab must open inside the click, so it can't be fetched then).
  const [logoDataUri, setLogoDataUri] = useState<string | null>(null)
  // …and the firm's document colours + fonts (null = the defaults).
  const [branding, setBranding] = useState<Pick<CasePdfOptions, 'accentColour' | 'secondaryColour' | 'headingFont' | 'bodyFont'>>({})
  useEffect(() => {
    if (!isOpen) return
    let alive = true
    void getFirmLogoApi().then(
      (l) => {
        if (!alive) return
        setLogoDataUri(l.dataUri)
        setBranding({ accentColour: l.accentColour ?? null, secondaryColour: l.secondaryColour ?? null, headingFont: l.headingFont ?? null, bodyFont: l.bodyFont ?? null })
      },
      () => { if (alive) { setLogoDataUri(null); setBranding({}) } },
    )
    return () => { alive = false }
  }, [isOpen])
  // Set when the browser blocks the export pop-up window.
  const [popupError, setPopupError] = useState<string | null>(null)

  // Re-sync the selected format each time the dialog opens.
  useEffect(() => { if (isOpen) { setFormat(initialFormat); setPopupError(null) } }, [isOpen, initialFormat])

  const str = (k: string): string => (caseFlat[k] === null || caseFlat[k] === undefined ? '' : String(caseFlat[k]))
  const ref = caseRef({ id: str('id'), createdAt: str('createdAt'), clientSurname: str('clientSurname') })
  const name = caseName({ caseName: str('caseName'), clientForename: str('clientForename'), clientSurname: str('clientSurname'), defendantName: str('defendantName') })
  const csvName = caseCsvFilename(ref)

  // Fields per section at the current detail level (copyOnly control flags excluded).
  const fieldCount = useMemo(() => {
    const m: Record<string, number> = {}
    for (const g of CASE_SECTION_GROUPS) {
      const fields = (detailLevel === 'summary' ? g.fields.filter((f) => g.summaryKeys.includes(f.key)) : g.fields).filter((f) => !f.copyOnly)
      m[g.key] = fields.length
    }
    return m
  }, [detailLevel])

  const toggleSection = (key: string): void => {
    setSections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const nothingSelected = sections.size === 0 && !includeWitnesses && !includeDeadlines

  const handleExportPdf = (): void => {
    setPopupError(null)
    const opts: CasePdfOptions = {
      sections, detailLevel, hideEmpty, letterhead, dark, includeWitnesses, includeDeadlines,
      firmName, logoDataUri, ...branding, witnesses, deadlines,
      generatedBy: authUser ? `${authUser.firstName ?? ''} ${authUser.lastName ?? ''}`.trim() || null : null,
    }
    // exportCasePdf opens a new tab synchronously (this handler is the user
    // gesture — no await before it). Returns false if the pop-up was blocked.
    const ok = exportCasePdf(caseFlat, opts)
    if (!ok) {
      setPopupError('Your browser blocked the export window. Please allow pop-ups for this site, then try again.')
      return
    }
    onClose()
  }

  const handleDownloadCsv = (): void => {
    // The same CSV the list/menu exported directly — one row, 128 columns.
    downloadCasesCsv([caseFlat as unknown as RawCase], csvName)
    onClose()
  }

  const fmtCard = (f: ExportFormat): React.ReactNode => {
    const on = format === f
    const pdf = f === 'pdf'
    return (
      <button type="button" role="radio" aria-checked={on} onClick={() => setFormat(f)} className={`${FC} ${on ? FC_ON : FC_OFF}`}>
        <span className={`${FC_IC} ${pdf
          ? 'bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0]'
          : 'bg-[#e5f0fa] text-[#2f6aa3] dark:bg-[rgba(124,174,230,0.15)] dark:text-[#9cc3ef]'}`}>
          <Ic d={pdf ? P.pdf : P.csv} className="w-[17px] h-[17px]" />
        </span>
        <span className="min-w-0">
          <span className="block font-bold text-[14px] text-casevo-forest">{pdf ? 'PDF summary' : 'CSV data'}</span>
          <span className="block text-[12px] text-casevo-text-soft mt-[2px] leading-[1.4]">
            {pdf ? 'Branded, printable case summary' : 'Every case field, one row · for Excel / backup'}
          </span>
        </span>
        <span aria-hidden="true" className={`ml-auto mt-[2px] w-[18px] h-[18px] rounded-full border-2 shrink-0 ${on
          ? 'border-casevo-primary dark:border-[#74c69d] bg-[radial-gradient(#2d6a4f_0_4px,transparent_5px)] dark:bg-[radial-gradient(#74c69d_0_4px,transparent_5px)]'
          : 'border-casevo-border'}`} />
      </button>
    )
  }

  const pdfBody = (
    <>
      {/* Detail (.rowline :52-53 + .seg :49-51) */}
      <div className="flex items-center gap-[14px] mb-[18px] flex-wrap">
        <div className={`${EB} !mb-0 w-[92px]`}>Detail</div>
        <div className="inline-flex border border-casevo-border rounded-[9px] overflow-hidden" role="radiogroup" aria-label="Detail level">
          {(['summary', 'full'] as const).map((lvl, i) => (
            <button key={lvl} type="button" role="radio" aria-checked={detailLevel === lvl} onClick={() => setDetailLevel(lvl)}
              className={`px-[16px] py-[7px] text-[13px] font-semibold focus:outline-none casevo-focus ${i ? 'border-l border-casevo-border' : ''} ${
                detailLevel === lvl ? 'bg-casevo-primary text-white' : 'bg-casevo-surface text-casevo-mid hover:text-casevo-forest'}`}>
              {lvl === 'summary' ? 'Summary' : 'Full'}
            </button>
          ))}
        </div>
        <span className="text-[12px] text-casevo-text-soft">Key fields only · Full = every field</span>
      </div>

      <div className={EB}>Sections</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-[8px] mb-[18px]">
        {CASE_SECTION_GROUPS.map((g, i) => {
          const on = sections.has(g.key)
          return <Chip key={g.key} on={on} label={g.label} onToggle={() => toggleSection(g.key)}
            n={on ? `${fieldCount[g.key]}${i === 0 ? ' fields' : ''}` : '—'} />
        })}
      </div>

      <div className={EB}>Also include</div>
      <div className="grid grid-cols-2 gap-[8px] mb-[18px]">
        <Chip on={includeWitnesses} label="Witnesses" n={String(witnesses.length)} onToggle={() => setIncludeWitnesses((v) => !v)} />
        <Chip on={includeDeadlines} label="Deadlines" n={String(deadlines.length)} onToggle={() => setIncludeDeadlines((v) => !v)} />
      </div>

      <div className={TOG}>
        Firm letterhead
        <span className="text-[12px] text-casevo-text-soft">{firmName || 'No firm name set'}</span>
        <Switch on={letterhead && !!firmName} disabled={!firmName} onChange={setLetterhead} label="Firm letterhead" />
      </div>
      <div className={TOG}>
        Hide empty fields
        <Switch on={hideEmpty} onChange={setHideEmpty} label="Hide empty fields" />
      </div>
      {/* .more (:82) — Dark background lives behind "More options". */}
      <button type="button" aria-expanded={moreOpen || dark} onClick={() => setMoreOpen((v) => !v)}
        className="flex items-center gap-[6px] text-[12.5px] font-semibold text-casevo-brand dark:text-[#8fd6b0] mt-[6px] mb-[4px] focus:outline-none casevo-focus rounded">
        <Ic d={P.chev} className={`w-[14px] h-[14px] transition-transform ${moreOpen || dark ? '' : '-rotate-90'}`} />
        More options · Dark background (screen / sharing)
      </button>
      {(moreOpen || dark) && (
        <div className={TOG}>
          Dark background
          <span className="text-[12px] text-casevo-text-soft">For screen / sharing — uses more ink when printed</span>
          <Switch on={dark} onChange={setDark} label="Dark background" />
        </div>
      )}
      {nothingSelected && <p className="text-[12px] text-casevo-text-soft mt-[6px]">Pick at least one section to export.</p>}
      {popupError && (
        <div role="alert" className="mt-[10px] px-3 py-2 rounded-lg text-xs bg-[#fbe9e6] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094]">{popupError}</div>
      )}
    </>
  )

  const shown = CSV_PREVIEW.filter((c) => CASE_EXPORT_COLUMNS.includes(c))
  const csvBody = (
    <>
      {/* .info (:76-77) */}
      <div className="flex gap-[10px] bg-casevo-input border border-casevo-border-soft rounded-[10px] px-[13px] py-[11px] text-[12.5px] leading-[1.5] text-casevo-forest mb-[14px]">
        <Ic d={P.info} className="w-[16px] h-[16px] mt-[1px] text-casevo-brand dark:text-[#8fd6b0]" />
        <div>One row with <b>all {CASE_EXPORT_COLUMNS.length} case columns</b> (client snapshot included). Witnesses, deadlines and documents aren’t in the CSV — use the PDF for those.</div>
      </div>
      <div className={EB}>Columns</div>
      {/* .colgrid (:78-79) */}
      <div className="flex flex-wrap gap-[6px] mb-[16px]">
        {shown.map((c) => (
          <span key={c} className="font-mono text-[11px] bg-casevo-input border border-casevo-border-soft rounded-[6px] px-[7px] py-[3px] text-casevo-text-soft">{c}</span>
        ))}
        <span className="font-mono text-[11px] bg-casevo-input border border-casevo-border-soft rounded-[6px] px-[7px] py-[3px] text-casevo-text-soft">+ {CASE_EXPORT_COLUMNS.length - shown.length} more</span>
      </div>
      <div className={EB}>File</div>
      {/* .fname (:81) */}
      <div className="flex items-center gap-[8px] border border-casevo-border rounded-[9px] px-[12px] py-[9px] font-mono text-[12.5px] bg-casevo-input text-casevo-forest mb-[8px] break-all">
        <Ic d={P.csv} className="w-[14px] h-[14px] text-casevo-text-soft" />{csvName}
      </div>
      <div className="text-[12px] text-casevo-text-soft mb-[6px]">UTF-8 with BOM — opens cleanly in Excel.</div>
    </>
  )

  const pdf = format === 'pdf'
  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      // Mockup .modal max-width 680px (:26).
      className="!max-w-[680px]"
      zIndexClassName="z-[80]"
      eyebrow="Export"
      eyebrowIcon={<Ic d={P.dl} className="w-[13px] h-[13px]" />}
      pills={<span className={HERO_PILL_SOFT}>{ref}</span>}
      title="Export case"
      subtitle={name}
      bodyPadding={false}
      bodyClassName="px-[28px] pt-[22px] pb-[6px]"
      footerLeft={
        <span className="flex items-center gap-[6px] text-[12px] text-casevo-text-soft">
          <Ic d={pdf ? P.ext : P.dl} />{pdf ? 'Opens in a new tab to print or save as PDF' : 'Downloads straight away'}
        </span>
      }
      footerRight={
        <>
          <button type="button" onClick={onClose} className={BTN2}>Cancel</button>
          {pdf ? (
            <button type="button" onClick={handleExportPdf} disabled={nothingSelected} className={BTN1}>
              <Ic d={P.pdf} className="w-[15px] h-[15px]" />Export PDF
            </button>
          ) : (
            <button type="button" onClick={handleDownloadCsv} className={BTN1}>
              <Ic d={P.dl} className="w-[15px] h-[15px]" />Download CSV
            </button>
          )}
        </>
      }
    >
      <div className={EB}>Format</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-[20px]" role="radiogroup" aria-label="Format">
        {fmtCard('pdf')}
        {fmtCard('csv')}
      </div>
      {pdf ? pdfBody : csvBody}
    </ModalFrame>
  )
}

export default ExportCaseDialog
